"use client";

// import { usePathname } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  RefObject,
} from "react";
//data
// import { useToast } from "./ToastContext";

import permissionsJson from "@/context/permissions.json";
import { getPostSignInRoute } from "@/utils/helpers/postSignInRoute";
import { createClient } from "@/utils/supabase/client";

// Define the shape of your company object
export interface CompanyType {
  id: number;
  name: string;
  roleId: number;
}

interface InvitationType {
  id: number;
  companyId: number;
  companyName: string;
}

export type LanguageCodeType = "en" | "es";

// Define the shape of your session object
export type SessionType = {
  userId: string;
  avatarUrl: string;
  email: string;
  locale: string;
  languageCode: LanguageCodeType;
  currencyCode: string;
  currencySymbol: string | null;
  timezone: string; // IANA name of the company; defines its calendar day
  usesHour12: boolean;
  profileId: number;
  companyId: number;
  companies: CompanyType[];
  defaultCompanyId: number | null;
  defaultCountryId: { id: number; code: string };
  revokedDate: Date | null;
  invitations: InvitationType[] | [];
  roleId: number;
  useUnits: boolean;
  subdomain?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  invoiceDataAvailable: boolean;
  tourCompleted: boolean;
  dataImportOnboardingShown: boolean;
  showTourButtons: boolean;
};

export const sessionInitialState: SessionType = {
  userId: "",
  avatarUrl: "",
  email: "",
  locale: "en-US",
  languageCode: "es",
  currencyCode: "USD",
  currencySymbol: "",
  timezone: "America/Costa_Rica",
  usesHour12: false, //!!hour12,
  profileId: 0, // Changed from 2 to 0 to avoid confusion with real profile IDs
  companyId: 0,
  companies: [],
  defaultCompanyId: 0,
  defaultCountryId: { id: 53, code: "CR" }, // Costa Rica
  revokedDate: null,
  invitations: [],
  roleId: 0, // no role until the server answers; 1 is Admin (SEC-16)
  useUnits: true,
  subdomain: null,
  userFirstName: null,
  userLastName: null,
  invoiceDataAvailable: false,
  tourCompleted: false,
  dataImportOnboardingShown: false,
  showTourButtons: true,
};

// 'unknown' hasta la primera respuesta real del servidor; solo pasa a
// 'unauthenticated' ante un 401/expiración confirmada, nunca ante un error
// transitorio (red caída, 5xx puntual) — eso evita cerrar sesión por un blip
// pasajero al reabrir la PWA desde background.
export type AuthStateType = "unknown" | "authenticated" | "unauthenticated";

interface SessionContextType {
  session: SessionType;
  setSession: (session: SessionType) => void;
  refreshSession: () => Promise<void>;
  waitForSessionUpdate: (
    condition: (session: SessionType) => boolean,
    maxAttempts?: number,
  ) => Promise<boolean>;
  checkPermission: (file: string, action: string) => boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  isLoggingOut: boolean;
  authState: AuthStateType;
  setIsLoggingOut: (isLoggingOut: boolean) => void;
  lastError: string | null;
  getNextRoute: () => string | null;
  portalContainerRef: RefObject<HTMLElement | null> | null;
  setPortalContainerRef: (ref: RefObject<HTMLElement | null> | null) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<SessionType>(
    sessionInitialState as SessionType,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [authState, setAuthState] = useState<AuthStateType>("unknown");
  const [portalContainerRef, setPortalContainerRef] =
    useState<RefObject<HTMLElement | null> | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second

  // Track re-renders to detect memory leaks - TEMPORARILY DISABLED
  // const { renderCount, propsChanges } = useRenderTracker("SessionProvider", {
  //   isLoading,
  //   isRefreshing,
  //   isLoggingOut,
  //   lastError,
  //   companyId: session.companyId,
  // });

  // Function to fetch session data from the API with retry logic
  const fetchSessionData = useCallback(async (isRetry = false) => {
    try {
      // Don't set loading on authentication pages
      if (
        !isRetry &&
        !(
          typeof window !== "undefined" &&
          (window.location.pathname === "/signin" ||
            window.location.pathname === "/signup" ||
            window.location.pathname === "/faq" ||
            window.location.pathname === "/privacy" ||
            window.location.pathname === "/terms" ||
            window.location.pathname === "/" ||
            window.location.pathname === "/features" ||
            window.location.pathname === "/pricing" ||
            window.location.pathname.includes("/candidates/") ||
            window.location.pathname.includes("/candidate/"))
        )
      ) {
        setIsLoading(true);
        setLastError(null);
      }

      console.log("Fetching session data...", {
        isRetry,
        retryCount: retryCountRef.current,
      });

      const response = await fetch(`/api/auth/session?t=${Date.now()}`, {
        credentials: "include", // Important for cookies
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (response.ok) {
        const sessionData = await response.json();

        // Fallback: If subdomain is null but we have a companyId, try to get it from hostname
        if (
          !sessionData.subdomain &&
          typeof window !== "undefined" &&
          sessionData.companyId
        ) {
          const hostname = window.location.hostname;
          const domain = process.env.NEXT_PUBLIC_DOMAIN || "orquesti.com";

          // Extract subdomain from hostname
          let extractedSubdomain: string | null = null;

          if (hostname.includes("localhost")) {
            if (
              hostname.includes(".") &&
              hostname.endsWith(".localhost:3000")
            ) {
              const parts = hostname.split(".");
              if (parts.length > 1 && parts[0] !== "www") {
                extractedSubdomain = parts[0];
              }
            }
          } else if (hostname.endsWith(`.${domain}`)) {
            extractedSubdomain = hostname.replace(`.${domain}`, "");
            if (extractedSubdomain === "www") {
              extractedSubdomain = null;
            }
          }

          if (extractedSubdomain) {
            console.log(
              "Extracted subdomain from hostname:",
              extractedSubdomain,
            );
            sessionData.subdomain = extractedSubdomain;
          }
        }

        setSession(sessionData);
        setAuthState("authenticated");
        retryCountRef.current = 0; // Reset retry count on success
        setLastError(null);
      } else if (response.status === 401) {
        // Not authenticated, clear session
        setSession(sessionInitialState);
        setAuthState("unauthenticated");
        retryCountRef.current = 0;
      } else if (response.status === 404) {
        // Candidates don't need session data, keep initial state
        setSession(sessionInitialState);
        setAuthState("unauthenticated");
        retryCountRef.current = 0;
      } else if (response.status === 429) {
        // Rate limited: retrying only keeps the counter above the limit, so stop at
        // the first 429 and say why. This is not a logout either.
        retryCountRef.current = 0;
        setLastError(
          "Demasiadas solicitudes. Espera un minuto e intenta de nuevo.",
        );
      } else {
        // Server error, retry if we haven't exceeded max retries
        if (retryCountRef.current < maxRetries) {
          retryCountRef.current++;
          console.log(
            `Retrying session fetch (attempt ${retryCountRef.current}/${maxRetries})`,
          );
          setTimeout(
            () => fetchSessionData(true),
            retryDelay * retryCountRef.current,
          );
          return;
        }

        // Falla transitoria del servidor (5xx): no la tratamos como logout —
        // dejamos la sesión existente intacta y solo reportamos el error.
        console.error("Failed to fetch session:", response.statusText);
        setLastError(`Failed to fetch session: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error fetching session:", error);

      // Network error, retry if we haven't exceeded max retries
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        console.log(
          `Retrying session fetch after network error (attempt ${retryCountRef.current}/${maxRetries})`,
        );
        setTimeout(
          () => fetchSessionData(true),
          retryDelay * retryCountRef.current,
        );
        return;
      }

      // Error de red tras agotar reintentos: tampoco es un logout — puede ser
      // la PWA reanudando desde background con la conexión aún inestable.
      setLastError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    } finally {
      if (!isRetry) {
        setIsLoading(false);
      }
    }
  }, []); // Empty dependency array is correct here since we don't want this to recreate

  // Auto-fetch session data on mount (including page refresh)
  // Skip session check for authentication pages to avoid unnecessary loading.
  // Only once per provider: the root <Suspense> in app/layout.tsx sits above this
  // provider, so whenever a page below suspends (loading its code, say) React hides
  // the tree and re-runs its mount effects when it resolves, keeping the state.
  // Fetching again there toggled isLoading and put "Cargando sesión" back over a
  // page that was already loaded (seen right after creating a company).
  const hasFetchedOnMountRef = useRef(false);
  useEffect(() => {
    if (hasFetchedOnMountRef.current) return;
    hasFetchedOnMountRef.current = true;

    // Check if we're on an authentication page
    const isAuthPage =
      typeof window !== "undefined" &&
      (window.location.pathname === "/signin" ||
        window.location.pathname === "/signup" ||
        window.location.pathname === "/faq" ||
        window.location.pathname === "/privacy" ||
        window.location.pathname === "/terms" ||
        window.location.pathname === "/" ||
        window.location.pathname === "/features" ||
        window.location.pathname === "/pricing" ||
        window.location.pathname.includes("/candidates/") ||
        window.location.pathname.includes("/candidate/"));

    if (!isAuthPage) {
      fetchSessionData();
    } else {
      console.log("Skipping session check for authentication page");
      setIsLoading(false);
    }
  }, []); // Only run once on mount

  // The web session is Supabase's (SEC-58). The browser client refreshes it on its
  // own and the middleware on every request, so there is no refresh loop here; this
  // only reacts when it ends (signed out here or in another tab, or its refresh
  // token revoked), so the page offers /signin instead of staying empty.
  useEffect(() => {
    const isAuthPage =
      typeof window !== "undefined" &&
      (window.location.pathname === "/signin" ||
        window.location.pathname === "/signup" ||
        window.location.pathname === "/faq" ||
        window.location.pathname === "/privacy" ||
        window.location.pathname === "/terms" ||
        window.location.pathname === "/" ||
        window.location.pathname === "/features" ||
        window.location.pathname === "/pricing" ||
        window.location.pathname.includes("/candidates/") ||
        window.location.pathname.includes("/candidate/"));

    if (isAuthPage) return;

    const {
      data: { subscription },
    } = createClient().auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_OUT") return;
      setLastError("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
      setSession(sessionInitialState);
      setAuthState("unauthenticated");
    });

    return () => subscription.unsubscribe();
  }, []);

  // Remove the old sessionStorage logic since we're now auto-fetching
  // useEffect(() => {
  //   // On mount, check localStorage for session
  //   const storedSession = sessionStorage.getItem("session");
  //   if (storedSession) {
  //     setSession(JSON.parse(storedSession));
  //     sessionStorage.removeItem("session");
  //   }
  // }, []);

  const refreshSession = async () => {
    setIsRefreshing(true);
    try {
      await fetchSessionData();
    } finally {
      setIsRefreshing(false);
    }
  };

  const waitForSessionUpdate = async (
    condition: (session: SessionType) => boolean,
    maxAttempts: number = 10,
  ): Promise<boolean> => {
    const pollInterval = 500; // 500ms between attempts (reduced frequency)

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Only make one API call per attempt
      try {
        const response = await fetch(`/api/auth/session?t=${Date.now()}`, {
          credentials: "include",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });

        // Rate limited: polling again would only make it worse, so give up now.
        if (response.status === 429) {
          setLastError(
            "Demasiadas solicitudes. Espera un minuto e intenta de nuevo.",
          );
          return false;
        }

        if (response.ok) {
          const sessionData = await response.json();

          // Update the session state
          setSession(sessionData);

          if (condition(sessionData)) {
            return true;
          }
        }
      } catch (error) {
        console.error("Error checking session data:", error);
      }

      if (attempt < maxAttempts - 1) {
        console.log(
          `Session update attempt ${attempt + 1}/${maxAttempts}, retrying...`,
        );
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }
    }

    console.warn("Session update timeout - condition not met");
    return false;
  };


  interface Permissions {
    [file: string]: {
      [action: string]: number[];
    };
  }

  const permissions: Permissions = permissionsJson as Permissions;

  const checkPermission = (file: string, action: string) => {
    // Check if the file exists in the permissions
    if (permissions[file]) {
      const actionPermissions = permissions[file][action];

      // Check if the action exists and if the user has the necessary permission
      if (actionPermissions && actionPermissions.includes(session.roleId)) {
        return true;
      }
    }
    console.log(`Permission was not defined for ${file} ${action}`);
    return false; // No permission if the conditions are not met
  };

  // Centralized routing logic based on session data
  const getNextRoute = (): string | null => {
    console.log("getNextRoute - Session data:", {
      invitations: session.invitations?.length || 0,
      companies: session.companies?.length || 0,
      defaultCompanyId: session.defaultCompanyId,
      companyId: session.companyId,
    });
    return getPostSignInRoute({
      invitationsCount: session.invitations?.length || 0,
      defaultCompanyId: session.defaultCompanyId,
    });
  };

  const value = useMemo(
    () => ({
      session,
      setSession,
      refreshSession,
      waitForSessionUpdate,
      checkPermission,
      isLoading,
      isRefreshing,
      isLoggingOut,
      setIsLoggingOut,
      authState,
      lastError,
      getNextRoute,
      portalContainerRef,
      setPortalContainerRef,
    }),
    [
      session,
      portalContainerRef,
      isLoading,
      isRefreshing,
      isLoggingOut,
      authState,
      lastError,
    ],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

// Change this line
export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};

export const useOptionalSession = () => {
  return useContext(SessionContext);
};
